import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";

import PropTypes from "prop-types";

// utils
import { getProductColumnClassName, getFormattedScoreOrNA } from "../../../../utils/analysisTable/analysisUtils";

// redux
import { useSelector } from "react-redux";

// components
import ConsumerFilter from "../../../common/ConsumerFilter";

import CircularProgress from "@mui/material/CircularProgress";
import { useMemo, useState } from "react";
import ProductInfo from "components/common/ProductInfo";

function StatsTable({ minHeight = 0, data = [], title = "", isPercentage = false, handleFilterSubmit = () => {}, products = [], tTest = {}, productsSecondaryData = [] }) {
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const { tTestResult } = tTest;
    const [productDetailsDialogueOpen, setProductDetailsDialogueOpen] = useState(false);
    const [selectedProductInfo, setSelectedProductInfo] = useState({});

    const filteredProductsSecondaryData = useMemo(() => [...new Map(productsSecondaryData.flatMap(({ scores }) => scores).map((score) => [score.title, score])).values()], [productsSecondaryData]);
    const handleProductClick = (e, info) => {
        setProductDetailsDialogueOpen(true);
        setSelectedProductInfo(info);
    };

    return (
        <>
            <TableContainer
                style={{
                    boxShadow: "inset rgb(219 219 219) 0px 0px 5px",
                    minHeight: "330px",
                }}
            >
                <Table
                    sx={{
                        minWidth: 450,
                    }}
                    size="small"
                    aria-label="a dense table"
                    style={{
                        minHeight: minHeight,
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                }}
                                align="left"
                            >
                                <TableSortLabel>{title}</TableSortLabel>
                            </TableCell>
                            {data?.map((datum, index) => {
                                const productTitle = `${datum?.title} / ${datum?.study_reference_title}`;
                                const key = `key-${index}`;
                                return (
                                    <TableCell
                                        key={key}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                        }}
                                        align="center"
                                    >
                                        <button
                                            className="clean-button color-blue"
                                            style={{
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                            }}
                                            onClick={(e) =>
                                                handleProductClick(e, {
                                                    coreDataId: datum?.coreDataId,
                                                    productCode: datum?.title,
                                                })
                                            }
                                        >
                                            {productTitle}
                                        </button>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow
                            sx={{
                                "&:last-child td, &:last-child th": {
                                    border: 0,
                                },
                            }}
                        >
                            <TableCell
                                component="td"
                                scope="row"
                                sx={{
                                    fontSize: "0.75rem",
                                }}
                                align="left"
                            >
                                {""}
                            </TableCell>
                            {data?.map((datum, index) => {
                                const productCodeTitle = datum.title;
                                const productCodeCoreDataId = datum.coreDataId;
                                const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                                return (
                                    <TableCell
                                        key={index + 1 + "cf"}
                                        className={className}
                                        sx={{
                                            fontSize: "0.75rem",
                                        }}
                                        align="center"
                                    >
                                        {products.length ? (
                                            <ConsumerFilter
                                                productsSecondaryData={products}
                                                product={datum}
                                                handleFilterSubmit={handleFilterSubmit}
                                            />
                                        ) : (
                                            <CircularProgress size={20} />
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                        <TableRow
                            // sx={{
                            //     "&:last-child td, &:last-child th": {
                            //         border: 0,
                            //     },
                            // }}
                            style={{
                                borderBottom: "1px solid rgba(224, 224, 224, 1)",
                            }}
                        >
                            <TableCell
                                component="td"
                                scope="row"
                                sx={{
                                    fontSize: "0.75rem",
                                    p: 2,
                                }}
                                align="left"
                            >
                                {isPercentage ? "Percentage" : "Count"}
                            </TableCell>
                            {data?.map((datum, index) => {
                                const productKey = `product-key-${index}`;
                                const scoreVal = isPercentage ? getFormattedScoreOrNA(datum?.percentage, "percentage") : datum?.count;
                                const filteredTestResult = tTestResult?.[0]?.colors?.find((ele) => ele.productCode.toLowerCase() == datum?.title.toLowerCase()) || {};
                                const testLabel = filteredTestResult?.label;
                                const testColor = filteredTestResult?.common;

                                return (
                                    <TableCell
                                        key={productKey}
                                        sx={{
                                            fontSize: "0.75rem",
                                            p: 2,
                                            backgroundColor: testColor ? testColor : "rgb(255, 255, 255)",
                                            borderRight: data.length - 1 === index ? "1px solid rgba(224, 224, 224, 1) !important" : 0,
                                        }}
                                        align="center"
                                    >
                                        {scoreVal}
                                        {testLabel && (
                                            <Typography
                                                component="span"
                                                sx={{ fontWeight: "bold", fontSize: "0.8rem", ml: 0.5, verticalAlign: "middle" }}
                                            >
                                                {testLabel}
                                            </Typography>
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            {productDetailsDialogueOpen && (
                <ProductInfo
                    settings={{
                        productDetailsDialogueOpen: productDetailsDialogueOpen,
                        setProductDetailsDialogueOpen: setProductDetailsDialogueOpen,
                    }}
                    info={selectedProductInfo}
                    productsSecondaryData={filteredProductsSecondaryData}
                />
            )}
        </>
    );
}

// Typechecking props for the StatsTable
StatsTable.propTypes = {
    title: PropTypes.string,
    minHeight: PropTypes.string,
    data: PropTypes.array,
    isPercentage: PropTypes.bool,
    handleFilterSubmit: PropTypes.func,
    products: PropTypes.array,
    tTest: PropTypes.object,
    productsSecondaryData: PropTypes.array,
};

export default StatsTable;
